import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon } from "./Icon";

interface RealtimeDataItem {
  id: string;
  key: string;
  value: any;
  dataType: string;
  metadata?: {
    source?: string;
    ttl?: number;
    description?: string;
  };
  expiresAt?: string;
  updatedAt: string;
  source: "internal" | "api" | "manual";
  isEnabled: boolean;
}

interface RealtimeDataManagerProps {
  authToken: string;
}

const RealtimeDataManager: React.FC<RealtimeDataManagerProps> = ({
  authToken,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dataItems, setDataItems] = useState<RealtimeDataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [newItem, setNewItem] = useState({
    key: "",
    value: "",
    ttl: "",
    description: "",
  });
  const [formattedContext, setFormattedContext] = useState<string>("");
  const [isMobile, setIsMobile] = useState(false);

  // Determine active tab from URL
  const getActiveTab = (): "data" | "manual" | "api" => {
    const path = location.pathname;
    if (path.includes("/realtime/manual")) return "manual";
    if (path.includes("/realtime/api")) return "api";
    return "data";
  };

  const activeTab = getActiveTab();

  useEffect(() => {
    fetchRealtimeData();

    // Refresh data every 30 seconds to remove expired items
    const interval = setInterval(() => {
      fetchRealtimeData();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const fetchRealtimeData = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/bot/realtime-data", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setDataItems(response.data.data);
      setFormattedContext(response.data.formatted);
    } catch (error) {
      console.error("Error fetching realtime data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.key) return;

    try {
      let value: any = newItem.value;

      // Try to parse as JSON if it looks like JSON
      if (newItem.value.startsWith("{") || newItem.value.startsWith("[")) {
        try {
          value = JSON.parse(newItem.value);
        } catch (e) {
          // Keep as string if JSON parse fails
        }
      } else if (!isNaN(Number(newItem.value))) {
        // Convert to number if it's a valid number
        value = Number(newItem.value);
      } else if (newItem.value === "true" || newItem.value === "false") {
        // Convert to boolean
        value = newItem.value === "true";
      }

      await axios.post(
        "/api/bot/realtime-data",
        {
          data: [
            {
              key: newItem.key,
              value,
              ttl: newItem.ttl ? parseInt(newItem.ttl) : undefined,
              metadata: {
                description: newItem.description || undefined,
              },
            },
          ],
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      // Reset form
      setNewItem({
        key: "",
        value: "",
        ttl: "",
        description: "",
      });

      fetchRealtimeData();
    } catch (error) {
      console.error("Error adding realtime data:", error);
      alert("Failed to add data item");
    }
  };

  const handleUpdateItem = async (item: RealtimeDataItem, newValue: string) => {
    try {
      let value: any = newValue;

      // Parse value based on data type
      if (item.dataType === "number") {
        value = Number(newValue);
      } else if (item.dataType === "boolean") {
        value = newValue === "true";
      } else if (item.dataType === "object" || item.dataType === "array") {
        try {
          value = JSON.parse(newValue);
        } catch (e) {
          alert("Invalid JSON format");
          return;
        }
      }

      await axios.post(
        "/api/bot/realtime-data",
        {
          data: [
            {
              key: item.key,
              value,
              metadata: item.metadata,
            },
          ],
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      setEditingItem(null);
      fetchRealtimeData();
    } catch (error) {
      console.error("Error updating realtime data:", error);
      alert("Failed to update data item");
    }
  };

  const handleDeleteItem = async (key: string) => {
    if (!window.confirm(`Delete data item "${key}"?`)) return;

    try {
      await axios.delete("/api/bot/realtime-data", {
        data: { keys: [key] },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      fetchRealtimeData();
    } catch (error) {
      console.error("Error deleting realtime data:", error);
      alert("Failed to delete data item");
    }
  };

  const handleToggleEnabled = async (key: string) => {
    try {
      await axios.patch(
        `/api/bot/realtime-data/${key}/toggle`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      fetchRealtimeData();
    } catch (error) {
      console.error("Error toggling realtime data:", error);
      alert("Failed to toggle data item");
    }
  };

  const formatValue = (value: any, dataType: string): string => {
    if (dataType === "object" || dataType === "array") {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  // Remove loading screen to prevent flash

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "center",
        gap: "20px",
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "0 20px",
      }}
    >
      <div
        style={{
          flex: "1",
          maxWidth: isMobile ? "100%" : "800px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <h3
          style={{ fontSize: "20px", margin: "0 0 24px 0", color: "#e2e8f0" }}
        >
          Real-time Data Management
        </h3>

        {/* Tab Navigation */}
        <div
          style={{
            display: "flex",
            gap: "4px",
            marginBottom: "20px",
            borderBottom: "2px solid #4a5568",
          }}
        >
          <button
            onClick={() => navigate("/dashboard/realtime")}
            style={{
              padding: "12px 24px",
              backgroundColor: activeTab === "data" ? "#9146FF" : "transparent",
              color: activeTab === "data" ? "white" : "#a0aec0",
              border: "none",
              borderRadius: "8px 8px 0 0",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: activeTab === "data" ? "600" : "normal",
              transition: "all 0.2s",
            }}
          >
            View Ingested Data
          </button>
          <button
            onClick={() => navigate("/dashboard/realtime/manual")}
            style={{
              padding: "12px 24px",
              backgroundColor:
                activeTab === "manual" ? "#9146FF" : "transparent",
              color: activeTab === "manual" ? "white" : "#a0aec0",
              border: "none",
              borderRadius: "8px 8px 0 0",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: activeTab === "manual" ? "600" : "normal",
              transition: "all 0.2s",
            }}
          >
            Manual Data Entry
          </button>
          <button
            onClick={() => navigate("/dashboard/realtime/api")}
            style={{
              padding: "12px 24px",
              backgroundColor: activeTab === "api" ? "#9146FF" : "transparent",
              color: activeTab === "api" ? "white" : "#a0aec0",
              border: "none",
              borderRadius: "8px 8px 0 0",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: activeTab === "api" ? "600" : "normal",
              transition: "all 0.2s",
            }}
          >
            Programmatic API Access
          </button>
        </div>

        {activeTab === "data" ? (
          <>
            {/* Current Data Items */}
            {dataItems.length > 0 ? (
              <div style={{ display: "grid", gap: "12px" }}>
                {dataItems.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      backgroundColor: "#2d3748",
                      padding: "16px",
                      borderRadius: "8px",
                      border: "1px solid #4a5568",
                      overflow: "hidden",
                      wordBreak: "break-word",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: "600",
                              fontSize: "16px",
                              color: item.isEnabled ? "#e2e8f0" : "#718096",
                              textDecoration: item.isEnabled
                                ? "none"
                                : "line-through",
                            }}
                          >
                            {item.key}
                          </span>
                          <span
                            style={{
                              fontSize: "12px",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              backgroundColor: "#4c1d95",
                              color: "#ddd6fe",
                            }}
                          >
                            {item.dataType}
                          </span>
                          <span
                            style={{
                              fontSize: "12px",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              backgroundColor:
                                item.source === "internal"
                                  ? "#065f46"
                                  : item.source === "api"
                                    ? "#1e40af"
                                    : "#7c2d12",
                              color:
                                item.source === "internal"
                                  ? "#6ee7b7"
                                  : item.source === "api"
                                    ? "#93c5fd"
                                    : "#fed7aa",
                            }}
                          >
                            {item.source}
                          </span>
                        </div>

                        {editingItem === item.id ? (
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                            }}
                          >
                            <input
                              type="text"
                              defaultValue={formatValue(
                                item.value,
                                item.dataType,
                              )}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleUpdateItem(
                                    item,
                                    (e.target as HTMLInputElement).value,
                                  );
                                } else if (e.key === "Escape") {
                                  setEditingItem(null);
                                }
                              }}
                              style={{
                                flex: 1,
                                padding: "6px 8px",
                                border: "1px solid #9146FF",
                                borderRadius: "4px",
                                fontSize: "14px",
                                backgroundColor: "#1a202c",
                                color: "#e2e8f0",
                              }}
                              autoFocus
                            />
                            <button
                              onClick={() => setEditingItem(null)}
                              style={{
                                padding: "6px 12px",
                                backgroundColor: "#4a5568",
                                color: "#e2e8f0",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#cbd5e0",
                              fontFamily:
                                item.dataType === "object" ||
                                item.dataType === "array"
                                  ? "monospace"
                                  : "inherit",
                              whiteSpace:
                                item.dataType === "object" ||
                                item.dataType === "array"
                                  ? "pre-wrap"
                                  : "normal",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                              maxWidth: "100%",
                              overflow: "auto",
                            }}
                          >
                            {formatValue(item.value, item.dataType)}
                          </div>
                        )}

                        {item.metadata?.description && (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#a0aec0",
                              marginTop: "4px",
                            }}
                          >
                            {item.metadata.description}
                          </div>
                        )}

                        <div
                          style={{
                            fontSize: "12px",
                            color: "#718096",
                            marginTop: "8px",
                          }}
                        >
                          Updated: {new Date(item.updatedAt).toLocaleString()}
                          {item.expiresAt && (
                            <span>
                              {" "}
                              • Expires:{" "}
                              {new Date(item.expiresAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={() => handleToggleEnabled(item.key)}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: item.isEnabled
                              ? "#166534"
                              : "#7f1d1d",
                            color: item.isEnabled ? "#bbf7d0" : "#fecaca",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                          }}
                        >
                          {item.isEnabled ? "Disable" : "Enable"}
                        </button>
                        <button
                          onClick={() => setEditingItem(item.id)}
                          disabled={
                            editingItem === item.id ||
                            item.source === "internal"
                          }
                          style={{
                            padding: "6px 12px",
                            backgroundColor:
                              item.source === "internal"
                                ? "#4a5568"
                                : "#4c1d95",
                            color:
                              item.source === "internal"
                                ? "#a0aec0"
                                : "#ddd6fe",
                            border: "none",
                            borderRadius: "4px",
                            cursor:
                              item.source === "internal"
                                ? "not-allowed"
                                : "pointer",
                            fontSize: "12px",
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteItem(item.key)}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#991b1b",
                            color: "#fecaca",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: "#2d3748",
                  padding: "40px",
                  borderRadius: "8px",
                  textAlign: "center",
                  color: "#a0aec0",
                  boxSizing: "border-box",
                  width: "100%",
                }}
              >
                <p style={{ margin: 0 }}>No real-time data configured yet.</p>
                <p style={{ margin: "8px 0 0 0", fontSize: "14px" }}>
                  Add data above to provide real-time context to the bot.
                </p>
              </div>
            )}

            {/* Context Preview */}
            {formattedContext && (
              <div
                style={{
                  marginTop: "20px",
                  backgroundColor: "#2d3748",
                  padding: "20px",
                  borderRadius: "8px",
                }}
              >
                <h4
                  style={{
                    marginTop: 0,
                    marginBottom: "12px",
                    color: "#e2e8f0",
                  }}
                >
                  Bot Context Preview
                </h4>
                <pre
                  style={{
                    backgroundColor: "#1a202c",
                    padding: "12px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    color: "#cbd5e0",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    maxWidth: "100%",
                    overflow: "auto",
                  }}
                >
                  {formattedContext}
                </pre>
              </div>
            )}
          </>
        ) : activeTab === "manual" ? (
          /* Manual Data Entry Tab */
          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "20px",
              borderRadius: "8px",
              marginBottom: "20px",
              border: "1px solid #4a5568",
              boxSizing: "border-box",
              width: "100%",
            }}
          >
            <h4
              style={{
                marginTop: 0,
                marginBottom: "16px",
                color: "#9146FF",
                fontSize: "18px",
              }}
            >
              Add or Update Data
            </h4>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {/* Key and TTL row */}
              <div>
                <div
                  style={{ display: "flex", gap: "12px", alignItems: "center" }}
                >
                  <input
                    type="text"
                    placeholder="Key (e.g., current_song)"
                    value={newItem.key}
                    onChange={(e) => {
                      // Only allow lowercase alphanumeric and underscore
                      const value = e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9_]/g, "");
                      setNewItem({ ...newItem, key: value });
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      border: "1px solid #4a5568",
                      borderRadius: "6px",
                      fontSize: "14px",
                      backgroundColor: "#1a202c",
                      color: "#e2e8f0",
                    }}
                  />
                  <input
                    type="text"
                    placeholder="TTL in seconds (optional)"
                    value={newItem.ttl}
                    onChange={(e) =>
                      setNewItem({ ...newItem, ttl: e.target.value })
                    }
                    style={{
                      width: isMobile ? "120px" : "180px",
                      padding: "8px 12px",
                      border: "1px solid #4a5568",
                      borderRadius: "6px",
                      fontSize: "14px",
                      backgroundColor: "#1a202c",
                      color: "#e2e8f0",
                    }}
                  />
                </div>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#718096",
                    margin: "4px 0 0 0",
                    fontStyle: "italic",
                  }}
                >
                  Keys can only contain lowercase letters, numbers, and
                  underscores
                </p>
              </div>

              {/* Value textarea */}
              <textarea
                placeholder="Value (string, number, boolean, or JSON)"
                value={newItem.value}
                onChange={(e) =>
                  setNewItem({ ...newItem, value: e.target.value })
                }
                rows={3}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  fontSize: "14px",
                  backgroundColor: "#1a202c",
                  color: "#e2e8f0",
                  resize: "vertical",
                  fontFamily: "monospace",
                  boxSizing: "border-box",
                }}
              />

              {/* Description */}
              <input
                type="text"
                placeholder="Description (optional)"
                value={newItem.description}
                onChange={(e) =>
                  setNewItem({ ...newItem, description: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  fontSize: "14px",
                  backgroundColor: "#1a202c",
                  color: "#e2e8f0",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <button
              onClick={handleAddItem}
              disabled={!newItem.key}
              style={{
                marginTop: "12px",
                padding: "8px 24px",
                backgroundColor: newItem.key ? "#9146FF" : "#4a5568",
                color: newItem.key ? "white" : "#718096",
                border: "none",
                borderRadius: "6px",
                cursor: newItem.key ? "pointer" : "not-allowed",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Add Data
            </button>
          </div>
        ) : (
          /* API Documentation Tab */
          <div
            style={{
              marginTop: "20px",
              backgroundColor: "#2d3748",
              padding: "20px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
              boxSizing: "border-box",
              width: "100%",
            }}
          >
            <h4
              style={{ marginTop: 0, marginBottom: "16px", color: "#e2e8f0" }}
            >
              <Icon
                name="rocket"
                size={18}
                style={{ verticalAlign: "-3px", marginRight: "8px" }}
              />
              Programmatic API Access
            </h4>

            <div style={{ marginBottom: "20px" }}>
              <h5
                style={{
                  color: "#9146FF",
                  marginBottom: "8px",
                  fontSize: "15px",
                }}
              >
                API Token Required
              </h5>
              <p
                style={{
                  color: "#cbd5e0",
                  fontSize: "13px",
                  margin: "0 0 8px 0",
                }}
              >
                Generate an API token from your{" "}
                <a href="/dashboard/account/api" style={{ color: "#9146FF" }}>
                  Account Settings
                </a>{" "}
                to use these endpoints.
              </p>
              <code
                style={{
                  backgroundColor: "#1a202c",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  color: "#10B981",
                }}
              >
                Authorization: hal_xxxxxxxxxxxxx
              </code>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <h5
                style={{
                  color: "#9146FF",
                  marginBottom: "12px",
                  fontSize: "15px",
                }}
              >
                API Endpoints
              </h5>

              {/* Create Data */}
              <div
                style={{
                  marginBottom: "16px",
                  backgroundColor: "#1a202c",
                  padding: "12px",
                  borderRadius: "6px",
                }}
              >
                <div style={{ marginBottom: "8px" }}>
                  <span
                    style={{
                      color: "#10B981",
                      fontWeight: "bold",
                      fontSize: "13px",
                    }}
                  >
                    POST
                  </span>
                  <code
                    style={{
                      color: "#cbd5e0",
                      marginLeft: "8px",
                      fontSize: "13px",
                    }}
                  >
                    /api/public/data
                  </code>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#a0aec0",
                    marginBottom: "8px",
                  }}
                >
                  Create a new real-time data entry
                </div>
                <pre
                  style={{
                    backgroundColor: "#0d1117",
                    padding: "8px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    margin: "0",
                    overflow: "auto",
                    color: "#cbd5e0",
                  }}
                >
                  {JSON.stringify(
                    {
                      key: "current_game",
                      value: "Elden Ring",
                      ttl: 3600,
                      description: "Currently playing game",
                    },
                    null,
                    2,
                  )}
                </pre>
              </div>

              {/* Get All Data */}
              <div
                style={{
                  marginBottom: "16px",
                  backgroundColor: "#1a202c",
                  padding: "12px",
                  borderRadius: "6px",
                }}
              >
                <div style={{ marginBottom: "8px" }}>
                  <span
                    style={{
                      color: "#3B82F6",
                      fontWeight: "bold",
                      fontSize: "13px",
                    }}
                  >
                    GET
                  </span>
                  <code
                    style={{
                      color: "#cbd5e0",
                      marginLeft: "8px",
                      fontSize: "13px",
                    }}
                  >
                    /api/public/data
                  </code>
                </div>
                <div style={{ fontSize: "12px", color: "#a0aec0" }}>
                  Get all real-time data entries
                </div>
              </div>

              {/* Get Specific Data */}
              <div
                style={{
                  marginBottom: "16px",
                  backgroundColor: "#1a202c",
                  padding: "12px",
                  borderRadius: "6px",
                }}
              >
                <div style={{ marginBottom: "8px" }}>
                  <span
                    style={{
                      color: "#3B82F6",
                      fontWeight: "bold",
                      fontSize: "13px",
                    }}
                  >
                    GET
                  </span>
                  <code
                    style={{
                      color: "#cbd5e0",
                      marginLeft: "8px",
                      fontSize: "13px",
                    }}
                  >
                    /api/public/data/:key
                  </code>
                </div>
                <div style={{ fontSize: "12px", color: "#a0aec0" }}>
                  Get specific data by key
                </div>
              </div>

              {/* Update Data */}
              <div
                style={{
                  marginBottom: "16px",
                  backgroundColor: "#1a202c",
                  padding: "12px",
                  borderRadius: "6px",
                }}
              >
                <div style={{ marginBottom: "8px" }}>
                  <span
                    style={{
                      color: "#F59E0B",
                      fontWeight: "bold",
                      fontSize: "13px",
                    }}
                  >
                    PUT
                  </span>
                  <code
                    style={{
                      color: "#cbd5e0",
                      marginLeft: "8px",
                      fontSize: "13px",
                    }}
                  >
                    /api/public/data/:key
                  </code>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#a0aec0",
                    marginBottom: "8px",
                  }}
                >
                  Update existing data entry
                </div>
                <pre
                  style={{
                    backgroundColor: "#0d1117",
                    padding: "8px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    margin: "0",
                    overflow: "auto",
                    color: "#cbd5e0",
                  }}
                >
                  {JSON.stringify(
                    {
                      value: "Dark Souls 3",
                      ttl: 7200,
                    },
                    null,
                    2,
                  )}
                </pre>
              </div>

              {/* Delete Data */}
              <div
                style={{
                  marginBottom: "16px",
                  backgroundColor: "#1a202c",
                  padding: "12px",
                  borderRadius: "6px",
                }}
              >
                <div style={{ marginBottom: "8px" }}>
                  <span
                    style={{
                      color: "#EF4444",
                      fontWeight: "bold",
                      fontSize: "13px",
                    }}
                  >
                    DELETE
                  </span>
                  <code
                    style={{
                      color: "#cbd5e0",
                      marginLeft: "8px",
                      fontSize: "13px",
                    }}
                  >
                    /api/public/data/:key
                  </code>
                </div>
                <div style={{ fontSize: "12px", color: "#a0aec0" }}>
                  Delete specific data entry
                </div>
              </div>

              {/* Delete All Data */}
              <div
                style={{
                  backgroundColor: "#1a202c",
                  padding: "12px",
                  borderRadius: "6px",
                }}
              >
                <div style={{ marginBottom: "8px" }}>
                  <span
                    style={{
                      color: "#EF4444",
                      fontWeight: "bold",
                      fontSize: "13px",
                    }}
                  >
                    DELETE
                  </span>
                  <code
                    style={{
                      color: "#cbd5e0",
                      marginLeft: "8px",
                      fontSize: "13px",
                    }}
                  >
                    /api/public/data?confirm=yes
                  </code>
                </div>
                <div style={{ fontSize: "12px", color: "#a0aec0" }}>
                  Delete all data entries (requires confirmation)
                </div>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <h5
                style={{
                  color: "#9146FF",
                  marginBottom: "8px",
                  fontSize: "15px",
                }}
              >
                Example cURL Request
              </h5>
              <pre
                style={{
                  backgroundColor: "#1a202c",
                  padding: "12px",
                  borderRadius: "6px",
                  fontSize: "11px",
                  margin: "0",
                  overflow: "auto",
                  color: "#cbd5e0",
                }}
              >
                {`curl -X POST http://localhost/api/public/data \\
  -H "Authorization: hal_your_api_token_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "key": "sub_goal",
    "value": {"current": 45, "target": 50},
    "ttl": 3600,
    "description": "Subscriber goal for today"
  }'`}
              </pre>
            </div>

            <div>
              <h5
                style={{
                  color: "#9146FF",
                  marginBottom: "8px",
                  fontSize: "15px",
                }}
              >
                Response Format
              </h5>
              <pre
                style={{
                  backgroundColor: "#1a202c",
                  padding: "12px",
                  borderRadius: "6px",
                  fontSize: "11px",
                  margin: "0",
                  overflow: "auto",
                  color: "#cbd5e0",
                }}
              >
                {JSON.stringify(
                  {
                    success: true,
                    message: "Real-time data created successfully",
                    data: {
                      key: "sub_goal",
                      value: { current: 45, target: 50 },
                      ttl: 3600,
                      description: "Subscriber goal for today",
                      createdAt: "2024-01-27T12:00:00.000Z",
                    },
                  },
                  null,
                  2,
                )}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Real-time Data Info Panel */}
      <div
        style={{
          width: isMobile ? "100%" : "350px",
          maxWidth: isMobile ? "800px" : "350px",
          backgroundColor: "rgba(45, 55, 72, 0.95)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
          borderRadius: "8px",
          padding: "20px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
          height: "fit-content",
          position: isMobile ? "static" : "sticky",
          top: "80px",
          order: isMobile ? 2 : 0,
          boxSizing: "border-box",
        }}
      >
        <h3
          style={{
            color: "#e2e8f0",
            marginTop: 0,
            marginBottom: "20px",
            fontSize: "18px",
          }}
        >
          <Icon
            name="radio"
            size={18}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          Real-time Data Guide
        </h3>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="key"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            What is Real-time Data?
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              fontSize: "13px",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            Dynamic information that HAL can access during conversations, like
            current song, game status, or stream goals.
          </p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="database"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            Data Types
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>
              <strong>String</strong> - Text values
            </li>
            <li>
              <strong>Number</strong> - Numeric values
            </li>
            <li>
              <strong>Boolean</strong> - true/false
            </li>
            <li>
              <strong>Object/Array</strong> - Complex JSON data
            </li>
          </ul>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="clock"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            TTL (Time To Live)
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              fontSize: "13px",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            Set expiration time in seconds. Data auto-deletes after TTL expires.
            Leave empty for permanent data.
          </p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="bar-chart-3"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            Data Sources
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>
              <strong>Manual</strong> - Data you add via dashboard
            </li>
            <li>
              <strong>API</strong> - Data from external integrations
            </li>
            <li>
              <strong>Internal</strong> - System-generated data (read-only)
            </li>
          </ul>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="pencil"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            Example Uses
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>current_game: &quot;Elden Ring&quot;</li>
            <li>sub_goal: {"{ current: 45, target: 50 }"}</li>
            <li>stream_topic: &quot;Speedrun attempts&quot;</li>
            <li>now_playing: &quot;Artist - Song Title&quot;</li>
          </ul>
        </div>

        <div
          style={{
            backgroundColor: "#1a202c",
            padding: "12px",
            borderRadius: "6px",
            marginTop: "15px",
            border: "1px solid #4a5568",
          }}
        >
          <p
            style={{
              color: "#9146FF",
              fontSize: "12px",
              margin: "0 0 6px 0",
              fontWeight: "bold",
            }}
          >
            <Icon
              name="lightbulb"
              size={14}
              style={{ verticalAlign: "-2px", marginRight: "6px" }}
            />
            Pro Tips:
          </p>
          <ul
            style={{
              color: "#a0aec0",
              fontSize: "11px",
              margin: 0,
              paddingLeft: "16px",
            }}
          >
            <li>HAL uses this data for contextual responses</li>
            <li>Update via API for automation</li>
            <li>Internal data is system-managed and read-only</li>
            <li>Preview shows how HAL sees the data</li>
            <li>Use descriptive keys for clarity</li>
          </ul>
        </div>

        <div
          style={{
            backgroundColor: "#4c1d95",
            padding: "12px",
            borderRadius: "6px",
            marginTop: "15px",
            border: "1px solid #6b21a8",
          }}
        >
          <p
            style={{
              color: "#e9d5ff",
              fontSize: "12px",
              margin: "0 0 6px 0",
              fontWeight: "bold",
            }}
          >
            <Icon
              name="star"
              size={14}
              style={{ verticalAlign: "-2px", marginRight: "6px" }}
            />
            Premium Feature
          </p>
          <p style={{ color: "#ddd6fe", fontSize: "11px", margin: 0 }}>
            Real-time data allows dynamic context that adapts to your stream in
            real-time, perfect for integrations and automation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RealtimeDataManager;
